import { useState, useEffect, useRef, useCallback } from 'react';
import useCreateOrUpdateAffordaPortalFormsMutation from './useCreateOrUpdateAffordaPortalFormsMutation';
import useDeleteAffordaPortalFormMutation from './useDeleteAffordaPortalFormMutation';
import useAffordaPortalFormsQuery from './useAffordaPortalFormsQuery';
import { INITIAL_STATE } from 'hooks/affordaPortalFormState/constants';
import useEnableFormStateLogging from 'hooks/affordaPortalFormState/useEnableFormStateLogging';
import debugLogger from 'utils/logger';

const useFormStateData = () => {
  const { isFormStateLoggingEnabled } = useEnableFormStateLogging();
  const shouldUpdateDB = useRef(true);
  const hasInitialized = useRef(false);
  const initialCreateAttempted = useRef(false);
  const [state, setState] = useState(INITIAL_STATE);

  const logger = useCallback(
    (message, data, status) => {
      if (!isFormStateLoggingEnabled) return;
      debugLogger('Form State', message, data, status);
    },
    [isFormStateLoggingEnabled],
  );

  const { isFetched } = useAffordaPortalFormsQuery({
    options: {
      onSuccess: (response) => {
        logger('Database Response', response);
        const hasData = !!response.id;
        if (!hasData) return logger('No Data');

        logger('Database Has Data');

        if (hasInitialized.current === true)
          return logger('Skipping setState: Already Initialized');

        hasInitialized.current = true;
        shouldUpdateDB.current = false;

        setState(response);
        logger('Initialized', response, 'success');
      },
    },
  });

  const { mutate } = useCreateOrUpdateAffordaPortalFormsMutation();
  const { mutate: deleteForm } = useDeleteAffordaPortalFormMutation();

  const update = useCallback(
    (newState) => {
      setState((prevState) => {
        const stateUpdate =
          typeof newState === 'function' ? newState(prevState) : newState;

        logger('Updates', stateUpdate);

        return stateUpdate;
      });
    },
    [logger],
  );

  // Update DB when state changes
  useEffect(() => {
    if (!isFetched) return logger('Not Fetched');

    // Skip the first update after initialization
    if (!shouldUpdateDB.current) {
      shouldUpdateDB.current = true;
      logger('Skipping DB Update');
      return;
    }

    // Special handling for state with null id (matches INITIAL_STATE)
    if (state.id === INITIAL_STATE.id) {
      logger('State id === INITIAL_STATE.id');
      // Allow first create attempt but prevent subsequent ones to avoid overwriting data
      if (initialCreateAttempted.current) {
        logger(
          'Prevented DB update with initial state at',
          {
            message: `Prevented DB update with initial state at ${new Date().toISOString()} - missing ID would overwrite existing data`,
            stateStructure: {
              id: state.id,
              portalUserId: state.portalUserId,
              applicantId: state.applicantId,
              applicationId: state.applicationId,
              hasInitialized: hasInitialized.current,
              isInitialState: state.id === INITIAL_STATE.id,
              initialCreateAttempted: initialCreateAttempted.current,
            },
            stackTrace: new Error().stack,
          },
          'error',
        );
        return;
      }

      // Allow the first creation attempt
      logger('Initial Create Attempt', {
        message:
          'Allowing initial create with null ID to create a database record',
        currentState: state,
      });
      initialCreateAttempted.current = true;
    }

    mutate(state);

    logger('Database Update', {
      timestamp: new Date().toISOString(),
      stateId: state.id,
      initialCreateAttempted: initialCreateAttempted.current,
      fullState: state,
    });
  }, [mutate, state, isFetched, logger]);

  return {
    data: state,
    update,
    deleteForm,
    isInitialized: hasInitialized.current,
  };
};

export default useFormStateData;
